import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Trash2 } from "lucide-react";
import { COLORS } from "../hooks/useAudioRecorder";
import { useApiClient } from "../api/client";
import { listInvoices, setInvoicePaid, deleteInvoice } from "../api/invoices";
import PageShell from "../components/PageShell";
import AuthChip from "../components/AuthChip";
import BackLink from "../components/BackLink";
import BrandMark from "../components/BrandMark";
import AppFooter from "../components/AppFooter";
import TrashModal from "../components/TrashModal";

const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  return `$${v.toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function Invoices() {
  const api = useApiClient();
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [trashOpen, setTrashOpen] = useState(false);
  const trashTriggerRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    listInvoices(api)
      .then((rows) => mounted && setItems(rows))
      .catch((e) => {
        if (!mounted) return;
        setError(e?.response?.data?.detail || e?.message || String(e));
      });
    return () => {
      mounted = false;
    };
  }, [api]);

  const handleRemove = (invoiceId) => {
    setItems((prev) =>
      prev ? prev.filter((it) => it.invoice_id !== invoiceId) : prev,
    );
  };

  const handlePaidChange = (invoiceId, paid) => {
    setItems((prev) =>
      prev
        ? prev.map((it) =>
            it.invoice_id === invoiceId ? { ...it, paid } : it,
          )
        : prev,
    );
  };

  // Restored from trash: prepend so the user sees it immediately. The list
  // sort (created_at DESC) is intentionally violated for the optimistic
  // surface — refreshing the page reconciles it.
  const handleRestored = (restored) => {
    if (!restored?.invoice_id) return;
    setItems((prev) => {
      const base = prev ? prev.filter((it) => it.invoice_id !== restored.invoice_id) : [];
      return [restored, ...base];
    });
  };

  return (
    <PageShell testId="invoices-page" padding="22px 22px 28px" maxWidth={720}>
      <ListStyles />

      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <BackLink to="/" label="Home" testId="invoices-back-link" />
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <AuthChip />
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <BrandMark size="header" />
            <span
              style={{
                fontFamily: "'Instrument Serif', Georgia, serif",
                fontStyle: "italic",
                fontSize: 22,
                lineHeight: 1,
              }}
            >
              HandInvoice
            </span>
          </div>
        </div>
      </header>

      <h1
        data-testid="invoices-title"
        style={{
          fontFamily: "'Instrument Serif', Georgia, serif",
          fontStyle: "italic",
          fontSize: 30,
          margin: "12px 0 6px",
          letterSpacing: "-0.005em",
        }}
      >
        Your Invoices
      </h1>

      <div
        data-testid="invoices-beta-notice"
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 10,
          color: "#5c584f",
          letterSpacing: "0.05em",
          lineHeight: 1.6,
        }}
      >
        // Beta — invoices saved to your account. Audio recordings are not stored.
      </div>
      <div
        style={{
          textAlign: "right",
          marginBottom: 18,
          marginTop: 2,
        }}
      >
        <button
          ref={trashTriggerRef}
          data-testid="invoices-trash-trigger"
          onClick={() => setTrashOpen(true)}
          aria-haspopup="dialog"
          style={{
            background: "transparent",
            border: "none",
            padding: 0,
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 10,
            letterSpacing: "0.05em",
            color: "#5c584f",
            cursor: "pointer",
            lineHeight: 1.6,
            transition: "color 160ms ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#f4f1ea")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#5c584f")}
        >
          // recently deleted →
        </button>
      </div>
      <TrashModal
        isOpen={trashOpen}
        onClose={() => setTrashOpen(false)}
        onRestored={handleRestored}
        api={api}
        triggerRef={trashTriggerRef}
      />

      {error && (
        <div
          data-testid="invoices-error"
          role="alert"
          style={{
            padding: "12px 14px",
            borderRadius: 12,
            border: `1px solid ${COLORS.red}`,
            backgroundColor: "rgba(229,75,60,0.08)",
            color: "#ffb3ab",
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12,
            lineHeight: 1.5,
            marginBottom: 14,
          }}
        >
          {error}
        </div>
      )}

      {items === null && !error && (
        <div
          data-testid="invoices-loading"
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12,
            color: COLORS.faint,
            textAlign: "center",
            paddingTop: 30,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          // loading
        </div>
      )}

      {items && items.length === 0 && (
        <div
          data-testid="invoices-empty"
          style={{
            marginTop: 30,
            padding: "28px 20px",
            border: `1px dashed ${COLORS.rule || "#3a362e"}`,
            borderRadius: 14,
            textAlign: "center",
            color: "#a8a29a",
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12,
            lineHeight: 1.7,
            letterSpacing: "0.05em",
          }}
        >
          No invoices yet. Tap record on home
          <br />
          to make your first one.
        </div>
      )}

      {items && items.length > 0 && (
        <ul
          data-testid="invoices-list"
          style={{ listStyle: "none", margin: 0, padding: 0 }}
        >
          {items.map((inv) => (
            <InvoiceRow
              key={inv.invoice_id}
              inv={inv}
              api={api}
              onRemove={handleRemove}
              onPaidChange={handlePaidChange}
              onError={setError}
            />
          ))}
        </ul>
      )}
      <AppFooter />
    </PageShell>
  );
}

function InvoiceRow({ inv, api, onRemove, onPaidChange, onError }) {
  const navigate = useNavigate();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const onTogglePaid = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    const next = !inv.paid;
    onPaidChange(inv.invoice_id, next); // optimistic
    setBusy(true);
    try {
      await setInvoicePaid(api, inv.invoice_id, next);
    } catch (err) {
      onPaidChange(inv.invoice_id, !next); // revert
      onError(err?.response?.data?.detail || err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const onConfirmDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await deleteInvoice(api, inv.invoice_id);
      onRemove(inv.invoice_id);
    } catch (err) {
      onError(err?.response?.data?.detail || err?.message || String(err));
      setConfirmingDelete(false);
      setBusy(false);
    }
  };

  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const goToDetail = () => navigate(`/invoices/${inv.invoice_id}`);
  const onRowKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      goToDetail();
    }
  };

  const dateLabel = fmtDate(inv.created_at);
  const numLabel = `#${inv.invoice_number || "—"}`;
  const customerName = inv.customer?.name;
  // balance_due is the live "what customer owes" number; if it's been driven
  // to zero (fully paid via deposit + payments), surface the gross total
  // so the row still reads as a real invoice rather than $0.
  const displayAmount =
    typeof inv.balance_due === "number" && inv.balance_due > 0
      ? inv.balance_due
      : inv.total_due;

  return (
    <li
      className="inv-row"
      data-testid={`invoices-row-${inv.invoice_id}`}
      onClick={goToDetail}
      onKeyDown={onRowKey}
      role="link"
      tabIndex={0}
    >
      {/* One unified layout at every width. Two rows:
            row 1 — customer name (left) · amount (right)
            row 2 — date · #num · status (left) · actions (right)
          No fixed-column table / breakpoint, so nothing overlaps and the
          fonts stay consistent across mobile and laptop. */}
      <span
        className="g-customer"
        style={customerName ? undefined : { color: "#5c584f", fontStyle: "italic" }}
      >
        {customerName || "no customer"}
      </span>
      <span className="g-amount">{fmtMoney(displayAmount)}</span>
      <span className="g-meta">
        <span className="g-meta-text">
          {dateLabel} · {numLabel}
        </span>
        <StatusPill status={inv.status} paid={!!inv.paid} />
      </span>

      <span className="g-actions" onClick={stop}>
        {confirmingDelete ? (
          <span
            data-testid={`invoices-row-${inv.invoice_id}-confirm`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            <button
              data-testid={`invoices-row-${inv.invoice_id}-confirm-yes`}
              onClick={onConfirmDelete}
              disabled={busy}
              style={{
                background: "transparent",
                border: `1px solid ${COLORS.red}`,
                color: "#ffb3ab",
                padding: "6px 12px",
                borderRadius: 999,
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                cursor: busy ? "not-allowed" : "pointer",
                opacity: busy ? 0.6 : 1,
              }}
            >
              Yes, delete
            </button>
            <button
              onClick={(e) => {
                stop(e);
                setConfirmingDelete(false);
              }}
              disabled={busy}
              style={{
                background: "transparent",
                border: `1px solid ${COLORS.rule || "#3a362e"}`,
                color: "#a8a29a",
                padding: "6px 12px",
                borderRadius: 999,
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                fontSize: 11,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                cursor: busy ? "not-allowed" : "pointer",
              }}
            >
              Cancel
            </button>
          </span>
        ) : (
          <>
            <button
              data-testid={`invoices-row-${inv.invoice_id}-paid-toggle`}
              onClick={onTogglePaid}
              disabled={busy}
              aria-label={inv.paid ? "Mark unpaid" : "Mark paid"}
              title={inv.paid ? "Mark unpaid" : "Mark paid"}
              style={{
                width: 44,
                height: 44,
                borderRadius: 999,
                border: `1px solid ${inv.paid ? "#3a4d35" : COLORS.rule || "#3a362e"}`,
                backgroundColor: inv.paid ? "rgba(111,201,111,0.10)" : "transparent",
                color: inv.paid ? "#9ed395" : "#a8a29a",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: busy ? "not-allowed" : "pointer",
                opacity: busy ? 0.6 : 1,
                padding: 0,
                flex: "0 0 auto",
              }}
            >
              <Check size={14} strokeWidth={2} />
            </button>
            <button
              data-testid={`invoices-row-${inv.invoice_id}-delete`}
              onClick={(e) => {
                stop(e);
                setConfirmingDelete(true);
              }}
              disabled={busy}
              aria-label="Delete invoice"
              title="Delete invoice"
              style={{
                width: 44,
                height: 44,
                borderRadius: 999,
                border: `1px solid ${COLORS.rule || "#3a362e"}`,
                backgroundColor: "transparent",
                color: "#a8a29a",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: busy ? "not-allowed" : "pointer",
                opacity: busy ? 0.6 : 1,
                padding: 0,
                flex: "0 0 auto",
              }}
            >
              <Trash2 size={13} strokeWidth={1.8} />
            </button>
          </>
        )}
      </span>
    </li>
  );
}

// Pill rendering logic for the status column. The `paid: bool` field
// (Phase-1 mark-paid toggle) wins over any non-paid status — so a finalized
// invoice the user has flagged Paid renders as Paid even though status
// still says "finalized". When paid=false, we render the status enum
// directly so future values (sent / viewed / overdue) flow through with
// no further code changes.
const STATUS_PILL_DISPLAY = {
  draft:     { label: "Draft",    color: "#a8a29a", border: "#3a362e", bg: "transparent" },
  finalized: { label: "Unpaid",   color: "#a8a29a", border: "#3a362e", bg: "transparent" },
  sent:      { label: "Sent",     color: "#a8a29a", border: "#3a362e", bg: "transparent" },
  viewed:    { label: "Viewed",   color: "#d6d2c3", border: "#3a362e", bg: "transparent" },
  paid:      { label: "Paid",     color: "#9ed395", border: "#3a4d35", bg: "rgba(111,201,111,0.08)" },
  overdue:   { label: "Overdue",  color: "#ffb3ab", border: "#5a322a", bg: "rgba(229,75,60,0.08)" },
};
const PAID_PILL_VALUE = STATUS_PILL_DISPLAY.paid;

function StatusPill({ status, paid }) {
  // paid=true is the authoritative override per the Phase-1 mark-paid flow.
  // For unknown statuses, fall back to the "finalized" pill to avoid blanks.
  const cfg = paid ? PAID_PILL_VALUE : (STATUS_PILL_DISPLAY[status] || STATUS_PILL_DISPLAY.finalized);
  return (
    <span
      data-testid={paid ? "invoices-pill-paid" : `invoices-pill-${status || "unknown"}`}
      style={{
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 9,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        padding: "2px 8px",
        borderRadius: 999,
        border: `1px solid ${cfg.border}`,
        color: cfg.color,
        backgroundColor: cfg.bg,
        whiteSpace: "nowrap",
      }}
    >
      {cfg.label}
    </span>
  );
}

/**
 * One unified layout at every width — no table, no breakpoint to break.
 *
 *   ┌─────────────────────────────────────────────┐
 *   │ Customer name                        $amount │   row 1
 *   │ MAY 29 · #20260529-1  [Unpaid]      [✓] [🗑] │   row 2
 *   └─────────────────────────────────────────────┘
 *
 * Two-column grid (content | trailing): customer + amount on top, the
 * date/#/status meta + actions below. The customer name and meta text both
 * carry min-width:0 + ellipsis so long names/numbers clip instead of
 * pushing into the amount/actions. The list container is capped to a
 * comfortable reading width by PageShell so it doesn't stretch sparse on a
 * wide laptop. Fonts are set explicitly on every cell (no inheritance).
 */
const ListStyles = () => (
  <style>{`
    .inv-row {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      grid-template-areas:
        "cust amt"
        "meta act";
      column-gap: 12px;
      row-gap: 5px;
      align-items: center;
      width: 100%;
      box-sizing: border-box;
      padding: 14px 0;
      border-bottom: 1px solid #2a2824;
      margin: 0;
      cursor: pointer;
    }
    .inv-row:focus-visible {
      outline: 1px solid #e8dc2d;
      outline-offset: 2px;
    }

    .g-customer {
      grid-area: cust;
      font-family: 'Inter', sans-serif;
      font-weight: 600;
      font-size: 16px;
      color: #f4f1ea;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }
    .g-amount {
      grid-area: amt;
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 15px;
      font-weight: 500;
      font-variant-numeric: tabular-nums;
      color: #f4f1ea;
      white-space: nowrap;
      justify-self: end;
    }
    .g-meta {
      grid-area: meta;
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
    }
    .g-meta-text {
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 11px;
      letter-spacing: 0.12em;
      color: #8a8472;
      text-transform: uppercase;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }
    .g-actions {
      grid-area: act;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      justify-self: end;
    }
  `}</style>
);
